import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Building2, Users, Activity, Megaphone, BookOpen, Trash2, Plus } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [users, setUsers] = useState([]);
  const [resources, setResources] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [tab, setTab] = useState("overview");
  const [newClinic, setNewClinic] = useState({ name: "", location: "", size: 0 });
  const [newAnn, setNewAnn] = useState({ title: "", body: "" });
  const [newRes, setNewRes] = useState({ title: "", category: "Stretching", url: "", description: "" });
  const [err, setErr] = useState("");

  const load = async () => {
    const [s, c, u, r, a] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/clinics"),
      api.get("/users"),
      api.get("/resources"),
      api.get("/announcements"),
    ]);
    setStats(s.data); setClinics(c.data); setUsers(u.data); setResources(r.data); setAnnouncements(a.data);
  };
  useEffect(() => { load(); }, []);

  const addClinic = async () => {
    setErr("");
    try { await api.post("/clinics", newClinic); setNewClinic({ name: "", location: "", size: 0 }); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };
  const delClinic = async (id) => { await api.delete(`/clinics/${id}`); load(); };
  const updateUser = async (uid, patch) => { await api.patch(`/users/${uid}`, patch); load(); };
  const delUser = async (uid) => { await api.delete(`/users/${uid}`); load(); };
  const addAnn = async () => { await api.post("/announcements", newAnn); setNewAnn({ title: "", body: "" }); load(); };
  const delAnn = async (id) => { await api.delete(`/announcements/${id}`); load(); };
  const addRes = async () => { await api.post("/resources", newRes); setNewRes({ title: "", category: "Stretching", url: "", description: "" }); load(); };

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "clinics", label: "Clinics" },
    { id: "users", label: "Users" },
    { id: "content", label: "Content" },
    { id: "announcements", label: "Announcements" },
  ];

  return (
    <div className="container-x py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="pill">Admin</span>
          <h1 className="mt-2 text-3xl font-bold">Sonote Platform Control</h1>
          <p className="text-zinc-500 text-sm mt-1">Signed in as {user?.email}</p>
        </div>
        <button onClick={logout} className="btn-ghost" data-testid="admin-logout">Sign out</button>
      </div>

      <div className="flex gap-2 flex-wrap mb-6 border-b border-zinc-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold ${tab === t.id ? "border-[#d4af37] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Users, label: "Total users", val: stats?.total_users ?? 0, tid: "admin-kpi-users" },
            { icon: Building2, label: "Clinics", val: stats?.total_clinics ?? 0, tid: "admin-kpi-clinics" },
            { icon: Activity, label: "Entries", val: stats?.total_entries ?? 0, tid: "admin-kpi-entries" },
            { icon: Users, label: "Sonographers", val: stats?.by_role?.sonographer ?? 0, tid: "admin-kpi-sono" },
          ].map((k) => (
            <div key={k.label} className="card-elev" data-testid={k.tid}>
              <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.15em] font-bold">
                <k.icon className="w-4 h-4" style={{ color: "var(--gold)" }} /> {k.label}
              </div>
              <div className="mt-3 text-3xl font-extrabold">{k.val}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "clinics" && (
        <div className="space-y-6">
          <div className="card-elev">
            <h3 className="font-bold mb-3">Add clinic</h3>
            <div className="grid sm:grid-cols-4 gap-3">
              <input placeholder="Name" value={newClinic.name} onChange={(e) => setNewClinic({ ...newClinic, name: e.target.value })} className="h-12 px-4 rounded-md border border-zinc-200" data-testid="admin-clinic-name" />
              <input placeholder="Location" value={newClinic.location} onChange={(e) => setNewClinic({ ...newClinic, location: e.target.value })} className="h-12 px-4 rounded-md border border-zinc-200" data-testid="admin-clinic-location" />
              <input placeholder="Size" type="number" value={newClinic.size} onChange={(e) => setNewClinic({ ...newClinic, size: Number(e.target.value) })} className="h-12 px-4 rounded-md border border-zinc-200" data-testid="admin-clinic-size" />
              <button onClick={addClinic} className="btn-gold" data-testid="admin-add-clinic"><Plus className="w-4 h-4 mr-1" />Add</button>
            </div>
            {err && <div className="text-red-600 text-sm mt-2">{err}</div>}
          </div>
          <div className="card-elev">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-[0.15em] text-zinc-500">
                <tr><th className="py-2">Name</th><th>Location</th><th>Size</th><th></th></tr>
              </thead>
              <tbody>
                {clinics.map((c) => (
                  <tr key={c.clinic_id} className="border-t border-zinc-100" data-testid={`clinic-row-${c.clinic_id}`}>
                    <td className="py-2 font-semibold">{c.name}</td>
                    <td>{c.location}</td>
                    <td>{c.size}</td>
                    <td className="text-right"><button onClick={() => delClinic(c.clinic_id)} className="text-red-600" data-testid={`delete-clinic-${c.clinic_id}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="card-elev overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-[0.15em] text-zinc-500">
              <tr><th className="py-2">Email</th><th>Name</th><th>Role</th><th>Clinic</th><th></th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-zinc-100" data-testid={`user-row-${u.user_id}`}>
                  <td className="py-2">{u.email}</td>
                  <td>{u.name}</td>
                  <td>
                    <select value={u.role} onChange={(e) => updateUser(u.user_id, { role: e.target.value })} className="border border-zinc-200 rounded-md px-2 py-1" data-testid={`user-role-${u.user_id}`}>
                      <option value="sonographer">sonographer</option>
                      <option value="clinic_manager">clinic_manager</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    <select value={u.clinic_id || ""} onChange={(e) => updateUser(u.user_id, { clinic_id: e.target.value || null })} className="border border-zinc-200 rounded-md px-2 py-1" data-testid={`user-clinic-${u.user_id}`}>
                      <option value="">—</option>
                      {clinics.map((c) => <option key={c.clinic_id} value={c.clinic_id}>{c.name}</option>)}
                    </select>
                  </td>
                  <td className="text-right"><button onClick={() => delUser(u.user_id)} className="text-red-600" data-testid={`delete-user-${u.user_id}`}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "content" && (
        <div className="space-y-6">
          <div className="card-elev">
            <h3 className="font-bold mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4" style={{ color: "var(--gold)" }} />Add resource</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Title" value={newRes.title} onChange={(e) => setNewRes({ ...newRes, title: e.target.value })} className="h-12 px-4 rounded-md border border-zinc-200" data-testid="admin-res-title" />
              <input placeholder="Category" value={newRes.category} onChange={(e) => setNewRes({ ...newRes, category: e.target.value })} className="h-12 px-4 rounded-md border border-zinc-200" data-testid="admin-res-category" />
              <input placeholder="URL" value={newRes.url} onChange={(e) => setNewRes({ ...newRes, url: e.target.value })} className="h-12 px-4 rounded-md border border-zinc-200 sm:col-span-2" data-testid="admin-res-url" />
              <textarea placeholder="Description" value={newRes.description} onChange={(e) => setNewRes({ ...newRes, description: e.target.value })} className="p-3 rounded-md border border-zinc-200 sm:col-span-2" rows="2" data-testid="admin-res-desc" />
              <button onClick={addRes} className="btn-gold sm:col-span-2" data-testid="admin-add-res">Add resource</button>
            </div>
          </div>
          <div className="card-elev">
            <h3 className="font-bold mb-3">Library</h3>
            <ul className="divide-y divide-zinc-100">
              {resources.length === 0 && <li className="text-sm text-zinc-500 py-3">No resources yet.</li>}
              {resources.map((r) => (
                <li key={r.id} className="py-3" data-testid={`res-row-${r.id}`}>
                  <div className="font-semibold">{r.title} <span className="text-xs uppercase tracking-[0.15em] text-zinc-500 ml-2">{r.category}</span></div>
                  <div className="text-sm text-zinc-600">{r.description}</div>
                  <a href={r.url} className="text-sm" style={{ color: "var(--gold-hover)" }} target="_blank" rel="noreferrer">{r.url}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === "announcements" && (
        <div className="space-y-6">
          <div className="card-elev">
            <h3 className="font-bold mb-3 flex items-center gap-2"><Megaphone className="w-4 h-4" style={{ color: "var(--gold)" }} />New announcement</h3>
            <input placeholder="Title" value={newAnn.title} onChange={(e) => setNewAnn({ ...newAnn, title: e.target.value })} className="w-full h-12 px-4 rounded-md border border-zinc-200" data-testid="admin-ann-title" />
            <textarea placeholder="Body" rows="3" value={newAnn.body} onChange={(e) => setNewAnn({ ...newAnn, body: e.target.value })} className="mt-3 w-full p-3 rounded-md border border-zinc-200" data-testid="admin-ann-body" />
            <button onClick={addAnn} className="mt-3 btn-gold" data-testid="admin-publish-ann">Publish</button>
          </div>
          <div className="card-elev">
            <ul className="divide-y divide-zinc-100">
              {announcements.length === 0 && <li className="text-sm text-zinc-500 py-3">No announcements.</li>}
              {announcements.map((a) => (
                <li key={a.id} className="py-3 flex items-start justify-between gap-4" data-testid={`ann-row-${a.id}`}>
                  <div>
                    <div className="font-semibold">{a.title}</div>
                    <div className="text-sm text-zinc-600">{a.body}</div>
                    <div className="text-xs text-zinc-400 mt-1">{a.created_at?.slice(0, 10)}</div>
                  </div>
                  <button onClick={() => delAnn(a.id)} className="text-red-600" data-testid={`delete-ann-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
